// Cliente HTTP da versão web (hospedada). O servidor roda o mesmo domínio; o
// navegador só manda comandos e recebe o Snapshot de volta.
//
// Uma única barreira HTTP (`requisicao`) no fundo deste módulo absorve tudo
// que o fetch tem de sujo: serialização, desserialização, e o ciclo de vida da
// sessão (401 → recarrega a página). As funções expostas ficam só com o nome
// do recurso — a complexidade de rede não atravessa o seam.

import type { Snapshot } from "../domain/snapshot.ts";
import type { Comando } from "../comandos/index.ts";

const JSON_HEADERS = { "content-type": "application/json" };

/**
 * Uma chamada HTTP arredondada: devolve o JSON da resposta ou lança. Com 401,
 * a sessão expirou — recarrega a página (a tela de login volta), salvo quando
 * `sessaoPodeFaltar` (endpoints que conhecem o estado "deslogado" e retornam
 * `null` em vez de forçar reload).
 */
async function requisicao<T>(
  url: string,
  opcoes: { metodo?: "GET" | "POST"; corpo?: unknown; sessaoPodeFaltar?: boolean } = {},
): Promise<T> {
  const r = await fetch(url, {
    method: opcoes.metodo ?? "GET",
    headers: JSON_HEADERS,
    ...(opcoes.corpo == null ? {} : { body: JSON.stringify(opcoes.corpo) }),
  });
  if (r.status === 401) {
    if (opcoes.sessaoPodeFaltar) return null as unknown as T;
    location.reload();
    throw new Error("Sessão expirada. Recarregando…");
  }
  if (!r.ok) {
    const j = (await r.json().catch(() => ({}))) as { erro?: unknown };
    throw new Error(
      typeof j.erro === "string" ? j.erro : "Não foi possível concluir a operação.",
    );
  }
  return (await r.json()) as T;
}

export async function apiLogin(
  login: string,
  senha: string,
): Promise<{ ok: true } | { ok: false; erro: string }> {
  try {
    const json = await requisicao<{ ok: true; usuario: unknown }>("/api/login", {
      metodo: "POST",
      corpo: { login, senha },
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, erro: e instanceof Error ? e.message : "Não foi possível entrar." };
  }
}

export async function apiLogout(): Promise<void> {
  await requisicao("/api/logout", { metodo: "POST" }).catch(() => {});
}

/** O Snapshot atual, ou `null` quando não há sessão (precisa fazer login). */
export async function apiSnapshot(): Promise<Snapshot | null> {
  return requisicao<Snapshot | null>("/api/snapshot", { sessaoPodeFaltar: true });
}

let eu: { id: string; nome: string } | null = null;

/** Busca (e guarda) o usuário logado. Chamado no boot hospedado. */
export async function apiEu(): Promise<{ id: string; nome: string } | null> {
  const j = await requisicao<{ id?: string; nome?: string } | null>("/api/eu", {
    sessaoPodeFaltar: true,
  });
  eu = j?.id ? { id: j.id, nome: j.nome ?? "" } : null;
  return eu;
}

/** O id do usuário logado, se já foi buscado (síncrono, pra render de tela). */
export function euIdAtual(): string | null {
  return eu?.id ?? null;
}

/** Manda um corpo JSON; o servidor devolve `{ snapshot }`. */
async function postJson(url: string, corpo: unknown): Promise<Snapshot> {
  return requisicao<{ snapshot: Snapshot }>(url, { metodo: "POST", corpo }).then(
    (j) => j.snapshot,
  );
}

/** Cria um usuário (a senha é hasheada no servidor). Devolve o Snapshot novo. */
export function apiCriarUsuario(dados: {
  nome: string;
  login: string;
  senha: string;
  perfilId: string | null;
}): Promise<Snapshot> {
  return postJson("/api/usuarios", dados);
}

/** Redefine a senha de um usuário. Devolve o Snapshot novo. */
export function apiRedefinirSenha(usuarioId: string, senha: string): Promise<Snapshot> {
  return postJson("/api/usuarios/senha", { usuarioId, senha });
}

/** Manda um comando pro servidor; devolve o Snapshot resultante ou lança. */
export function apiExecutar(comando: Comando): Promise<Snapshot> {
  return postJson("/api/comando", comando);
}

/** Uma página do Log de Auditoria (ADR-0012: lê fora do Snapshot). */
export interface RespostaAuditoria {
  linhas: {
    id: string;
    em: string;
    usuarioId: string | null;
    resumo: string;
    detalhe: string;
  }[];
  total: number;
  pagina: number;
  porPagina: number;
  agregados: string[];
}

export async function apiAuditoria(params: {
  usuarioId?: string;
  de?: string;
  ate?: string;
  resumo?: string;
  pagina?: number;
  porPagina?: number;
}): Promise<RespostaAuditoria> {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") q.set(k, String(v));
  }
  return requisicao<RespostaAuditoria>(`/api/auditoria?${q.toString()}`);
}
